// Admin disputes — the resolution side of "verified or refunded". Each open
// dispute holds the merchant's payout; resolving it issues a refund / charges
// the merchant (both withhold the payout) or closes with no action (releases
// the payout). Investigate with the order's proof-of-delivery photo.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, ShieldAlert, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { PortalShell } from '../../components/shared/PortalShell';
import { Card, EmptyState } from '../../components/shared/primitives';
import { Button } from '../../components/stripe-ui/Button';
import { ADMIN_NAV } from '../_navs';
import { admin, type Dispute, type DisputeStatus } from '../../lib/api';

const REASON_LABEL: Record<string, string> = {
  not_received: 'Never arrived', wrong_items: 'Wrong items', damaged: 'Damaged/spoiled',
  quantity_short: 'Quantity short', quality: 'Poor quality', other: 'Other',
};
const RESOLUTIONS: { v: DisputeStatus; label: string; hint: string }[] = [
  { v: 'refund_issued', label: 'Issue refund', hint: 'Customer refunded · payout withheld' },
  { v: 'merchant_charged', label: 'Charge merchant', hint: 'In customer’s favor · payout withheld' },
  { v: 'resolved_no_action', label: 'No action', hint: 'Order stands · payout released' },
];
const isOpen = (s: DisputeStatus) => s === 'open' || s === 'investigating';

export function AdminDisputesPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<'open' | 'all'>('open');
  const q = useQuery({ queryKey: ['admin', 'disputes', tab], queryFn: () => admin.disputes(tab === 'open' ? 'open' : undefined) });

  return (
    <PortalShell nav={ADMIN_NAV} activeId="disputes" title="Disputes" subtitle="The merchant payout is held until a case is resolved.">
      <div className="space-y-4 max-w-4xl">
        <div className="inline-flex rounded-lg border border-[var(--color-border)] bg-white p-0.5 text-[12.5px] font-semibold">
          {(['open', 'all'] as const).map((t) => (
            <button key={t} onClick={() => setTab(t)} className={`px-3 py-1.5 rounded-md capitalize ${tab === t ? 'bg-[var(--color-ink)] text-white' : 'text-[var(--color-secondary-2)] hover:text-[var(--color-ink)]'}`}>{t}</button>
          ))}
        </div>

        {q.isLoading ? (
          <Card><div className="text-center py-10 text-[var(--color-muted)] text-[13px]"><Loader2 className="inline w-4 h-4 animate-spin" /> Loading…</div></Card>
        ) : (q.data ?? []).length === 0 ? (
          <Card><EmptyState icon={ShieldAlert} title={tab === 'open' ? 'No open disputes' : 'No disputes'} body="When a customer reports a problem, it shows up here with the order's proof photo to investigate." /></Card>
        ) : (
          (q.data ?? []).map((d) => <DisputeRow key={d.id} d={d} onResolved={() => qc.invalidateQueries({ queryKey: ['admin', 'disputes'] })} />)
        )}
      </div>
    </PortalShell>
  );
}

function DisputeRow({ d, onResolved }: { d: Dispute; onResolved: () => void }) {
  const [pick, setPick] = useState<DisputeStatus | ''>('');
  const [note, setNote] = useState('');
  const ref = `REQ-${String(d.request_id).padStart(4, '0')}`;

  const resolve = useMutation({
    mutationFn: () => admin.resolveDispute(d.id, { status: pick as DisputeStatus, resolution: note.trim() || undefined }),
    onSuccess: () => { toast.success('Dispute resolved'); onResolved(); },
    onError: (e: Error) => toast.error(e.message || 'Failed'),
  });

  return (
    <Card>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="font-mono text-[13px] text-[#063B9E] inline-flex items-center gap-2">
            {ref}
            <a href={`/admin/requests/${d.request_id}`} className="text-[var(--color-muted)] hover:text-[var(--color-secondary)]"><ExternalLink className="w-3.5 h-3.5" /></a>
          </p>
          <p className="text-[14px] font-semibold text-[var(--color-ink)] mt-0.5">{REASON_LABEL[d.reason] ?? d.reason}</p>
          <p className="text-[12px] text-[var(--color-secondary-2)] mt-0.5">
            Opened by {d.opened_by}{d.request?.recipient?.full_name ? ` · for ${d.request.recipient.full_name}` : ''} · {new Date(d.created_at).toLocaleDateString()}
          </p>
          {d.details && <p className="text-[12.5px] text-[var(--color-body)] mt-1.5 bg-[var(--color-panel)] border border-[var(--color-hairline)] rounded-md px-2.5 py-1.5">“{d.details}”</p>}
          {!isOpen(d.status) && (
            <p className="text-[12px] text-[var(--color-success-fg)] mt-1.5"><b>Resolved:</b> {RESOLUTIONS.find((r) => r.v === d.status)?.label ?? d.status}{d.resolution ? ` — ${d.resolution}` : ''}</p>
          )}
        </div>
        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded shrink-0 ${isOpen(d.status) ? 'bg-[var(--color-warning-bg)] text-[var(--color-warning-fg)]' : 'bg-[var(--color-panel)] text-[var(--color-secondary-2)]'}`}>{isOpen(d.status) ? 'Open' : 'Resolved'}</span>
      </div>

      {isOpen(d.status) && (
        <div className="mt-3 pt-3 border-t border-[var(--color-hairline)] space-y-2.5">
          <div className="grid sm:grid-cols-3 gap-2">
            {RESOLUTIONS.map((r) => (
              <button key={r.v} onClick={() => setPick(r.v)} className={`text-left px-3 py-2 rounded-lg border transition-colors ${pick === r.v ? 'border-brand-500 bg-brand-50' : 'border-[var(--color-border)] hover:bg-[var(--color-panel)]'}`}>
                <span className="block text-[12.5px] font-semibold text-[var(--color-ink)]">{r.label}</span>
                <span className="block text-[10.5px] text-[var(--color-muted)]">{r.hint}</span>
              </button>
            ))}
          </div>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Resolution note (shared with the customer)…" className="w-full px-3 py-2 text-[13px] border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
          <Button size="sm" onClick={() => resolve.mutate()} loading={resolve.isPending} disabled={!pick}>Resolve dispute</Button>
        </div>
      )}
    </Card>
  );
}
